/**
 * The nav across the Edit Contest screens, which doubles as a readiness
 * indicator (design-system.md §6 "Setup rail"; `.sr-checklist` in the
 * prototype's setup-rail.css).
 *
 * Four of its segments are the design's setup steps and report progress. A
 * segment may also opt out of that arithmetic with `tracksSetup: false` — a
 * destination in the flow that readiness has no opinion about — so the strip can
 * reach a screen the handoff never drew without lying about how ready the
 * contest is.
 *
 * The prototype ships three variants — `stepper`, `checklist`, `progress`. This
 * is `checklist`, the one every Edit screen actually renders: all four pass no
 * `variant` prop and take its default, and the handoff's screenshots show it.
 *
 * Visually the sibling of `<SegmentedTabs>` — one rounded trough, the current
 * item riding a white pill — and structurally not: these are links. Each step is
 * a URL, so the strip gets `<nav>` and `aria-current="step"` rather than the
 * prototype's `role="tablist"` / `aria-selected`, which it inherited from the
 * tab strip it replaces. A tablist that navigates lies to a screen reader about
 * what pressing it does.
 *
 * ## Why a plain anchor and not `<Link>`
 *
 * `<Link>` hard-codes `aria-current="page"` on whichever step is active, and it
 * wins: the router spreads `...isActive && STATIC_ACTIVE_PROPS` *last*, after
 * both the caller's props and `activeProps`, so there is no way through its API
 * to say `step` instead. `page` is for one page among a set; `step` is for one
 * step of a process, which is exactly what a four-step setup rail is and what
 * the design calls it. (A segment with `tracksSetup: false` is the one that
 * genuinely wants `page` — it is a destination, not a step — and this component
 * picks per segment, which `<Link>` could not do either.)
 *
 * So the anchor is ours, with the modifier-key and non-primary-button guards
 * `<Link>` would have given us, and `navigate()` for the ordinary click. What is
 * given up is prefetch-on-hover, which buys nothing here: all four steps are
 * children of one already-loaded layout route.
 */
import { useNavigate } from '@tanstack/react-router';
import Box from '@mui/material/Box';
import { useId, type MouseEvent } from 'react';
import type { Theme } from '@mui/material/styles';
import type { SetupStep, SetupStepKey } from '../../contests/readiness';
import { CheckIcon, WarnIcon } from './line-icons';

export interface SetupRailStep extends Omit<SetupStep, 'key'> {
  /** A readiness step's key, or a section that only navigates. */
  key: SetupStepKey | 'lobby-images';
  /** The resolved path for this step — the layout interpolates the contest id. */
  to: string;
  /**
   * False for a segment that is a destination but not part of setup.
   *
   * Lobby images is the one: it belongs in the Edit flow — it is the only place
   * the artwork on a contest's lobby card can be changed — but it is not one of
   * the four steps the design counts, and nothing about a contest's readiness
   * turns on it. Counting it would make the strip read `n/5` and quietly move
   * every percentage, so these segments navigate, carry their count badge, and
   * stay out of the arithmetic and out of the ring treatment.
   *
   * Defaults to true, so the four readiness steps need say nothing.
   */
  tracksSetup?: boolean;
}

interface SetupRailProps {
  steps: readonly SetupRailStep[];
  /**
   * Undefined when the operator is on a route inside the Edit flow that no
   * segment covers. Nothing is then marked current, rather than the rail
   * guessing.
   */
  current?: SetupStepKey | 'lobby-images';
}

/**
 * What the step's glyph says out loud. The glyph is the only thing carrying it.
 *
 * Done and needing attention are not exclusive, and the sighted rendering says
 * so in two places at once: the glyph stays a check while the count badge turns
 * amber (which is what the prototype does — `state` falls through to `done`, and
 * `data-attn` is set on the badge independently). A step attached to a fixture
 * the feed has no markets for is complete *and* wants looking at. Saying only
 * "complete" would drop the amber half for anyone who cannot see it.
 */
/**
 * The width below which segments reflow instead of overflowing.
 *
 * `segmented-tabs.tsx` uses 132 for a label and a count badge; a rail segment
 * carries a ring as well, so it needs a little more before its label starts
 * clipping.
 */
const SEGMENT_MIN_WIDTH = 148;

const stateLabel = (step: SetupRailStep): string => {
  if (step.done) return step.attention ? 'complete, needs attention' : 'complete';
  return step.attention ? 'needs attention' : 'not started';
};

/**
 * Lets the browser handle every click that is not a plain left-click — cmd/ctrl
 * for a new tab, shift for a new window, middle-click — which is the whole point
 * of rendering a real `href` rather than a button.
 */
const isPlainLeftClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

export const SetupRail = ({ steps, current }: SetupRailProps) => {
  const navigate = useNavigate();
  // Only the readiness steps are counted — see `tracksSetup`.
  const setupSteps = steps.filter((step) => step.tracksSetup !== false);
  const doneCount = setupSteps.filter((step) => step.done).length;
  const total = setupSteps.length;
  const percent = total === 0 ? 0 : Math.round((doneCount / total) * 100);
  const allDone = total > 0 && doneCount === total;
  // `useId` rather than a constant: a hard-coded id would collide the moment a
  // screen rendered two rails, and silently point both descriptions at the first.
  const progressDescriptionId = useId();

  return (
    <Box
      component="nav"
      aria-label="Contest setup"
      sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box
          component="span"
          sx={(theme: Theme) => ({
            fontSize: theme.tokens.text.eyebrow.fontSize,
            fontWeight: theme.tokens.text.eyebrow.fontWeight,
            letterSpacing: theme.tokens.text.eyebrow.letterSpacing,
            textTransform: 'uppercase',
            color: theme.tokens.color.ink4,
          })}
        >
          Setup
        </Box>

        {/*
          One `progressbar` carrying the whole strip's state, rather than a bare
          pair of numbers: the count, the bar and the percentage are three
          renderings of one value, and a screen reader should hear it once.
        */}
        <Box
          role="progressbar"
          aria-label="Setup progress"
          aria-valuenow={doneCount}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-describedby={progressDescriptionId}
          aria-valuetext={`${doneCount} of ${total} steps complete`}
          sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}
        >
          {/*
            `3/4`, the handoff's own wording (`.sr-meta-value`, with the done
            count bold). Terse because it sits beside the bar and the percentage
            saying the same thing; `aria-valuetext` above carries the long form
            for anyone who cannot see the three together.
          */}
          <Box
            component="span"
            id={progressDescriptionId}
            sx={(theme: Theme) => ({ fontSize: '12.5px', color: theme.tokens.color.ink2 })}
          >
            <Box
              component="strong"
              sx={(theme: Theme) => ({ color: theme.tokens.color.ink, fontWeight: 600 })}
            >
              {doneCount}
            </Box>
            {`/${total}`}
          </Box>

          <Box
            aria-hidden
            sx={(theme: Theme) => ({
              flex: 1,
              maxWidth: '160px',
              height: '4px',
              borderRadius: `${theme.tokens.radius.pill}px`,
              overflow: 'hidden',
              backgroundColor: theme.tokens.componentColor.barTrack,
            })}
          >
            <Box
              sx={(theme: Theme) => ({
                height: '100%',
                width: `${percent}%`,
                transition: 'width 0.3s ease',
                backgroundColor: allDone ? theme.tokens.color.success : theme.tokens.color.accent,
              })}
            />
          </Box>

          <Box
            aria-hidden
            component="span"
            sx={(theme: Theme) => ({
              padding: '2px 7px',
              borderRadius: `${theme.tokens.radius.pill}px`,
              fontFamily: theme.tokens.font.mono,
              fontSize: '11.5px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              ...(allDone
                ? {
                    backgroundColor: theme.tokens.color.successSoft,
                    color: theme.tokens.color.success,
                  }
                : {
                    backgroundColor: theme.tokens.componentColor.barTrack,
                    color: theme.tokens.color.ink3,
                  }),
            })}
          >
            {`${percent}%`}
          </Box>
        </Box>
      </Box>

      <Box
        sx={(theme: Theme) => ({
          display: 'grid',
          /*
           * Wraps rather than overflowing, the same treatment and for the same
           * reason as `segmented-tabs.tsx` — which measured it: `1fr` is
           * `minmax(auto, 1fr)`, and that `auto` floor is the column's
           * min-content, so a strip cannot shrink below the sum of its widest
           * segments and takes the page's horizontal scrollbar with it when the
           * column is narrower.
           *
           * The handoff's flat `repeat(4, 1fr)` was close enough to survive at
           * four segments. A fifth — "Lobby images" plus a count badge — pushes
           * the floor past the admin's column at tablet widths. `auto-fit` still
           * resolves to one equal row wherever there is room, so the design's
           * rendering is unchanged where the design applies.
           */
          gridTemplateColumns: `repeat(auto-fit, minmax(min(${SEGMENT_MIN_WIDTH}px, 100%), 1fr))`,
          gap: '4px',
          padding: '5px',
          borderRadius: `${theme.tokens.radius.pill}px`,
          backgroundColor: theme.tokens.componentColor.ringTrack,
        })}
      >
        {steps.map((step) => {
          const isCurrent = step.key === current;

          return (
            <Box
              key={step.key}
              component="a"
              href={step.to}
              /*
               * `step` for a setup step, `page` for a segment that is only a
               * destination — the distinction this codebase already draws
               * between `milestone-track.tsx` and `admin-nav-rail.tsx`.
               *
               * Calling lobby images the current *step* would contradict the
               * progressbar in the same strip, which counts four steps and does
               * not include it. ARIA has a value for "one of a set of pages",
               * and that is what this segment is.
               */
              aria-current={isCurrent ? (step.tracksSetup === false ? 'page' : 'step') : undefined}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                if (!isPlainLeftClick(event)) return;
                event.preventDefault();
                void navigate({ to: step.to });
              }}
              sx={(theme: Theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: `${theme.tokens.radius.pill}px`,
                textDecoration: 'none',
                fontFamily: theme.tokens.font.sans,
                fontSize: theme.tokens.text.tab.fontSize,
                letterSpacing: '-0.005em',
                transition: 'background 0.12s, color 0.12s',
                ...(isCurrent
                  ? {
                      backgroundColor: theme.tokens.color.surface,
                      color: theme.tokens.color.ink,
                      fontWeight: theme.tokens.tabSelectedWeight,
                      boxShadow: theme.tokens.shadow.tabSelected,
                    }
                  : {
                      color: theme.tokens.color.ink3,
                      fontWeight: theme.tokens.text.tab.fontWeight,
                      '&:hover': { color: theme.tokens.color.ink2 },
                    }),
              })}
            >
              {/*
                No ring on a segment outside setup. A done/attn/idle glyph is a
                readiness verdict, and there is none to give for lobby images —
                an empty strip is a choice, not an omission, so an idle ring
                beside the four real steps would read as work outstanding.
              */}
              {step.tracksSetup === false ? null : (
                <Box
                  role="img"
                  aria-label={`${step.label}: ${stateLabel(step)}`}
                  sx={(theme: Theme) => ({
                    display: 'grid',
                    placeItems: 'center',
                    width: '16px',
                    height: '16px',
                    flexShrink: 0,
                    borderRadius: '50%',
                    ...(step.done
                      ? {
                          backgroundColor: theme.tokens.color.accent,
                          color: theme.tokens.color.surface,
                        }
                      : step.attention
                        ? {
                            backgroundColor: theme.tokens.color.warn,
                            color: theme.tokens.color.surface,
                          }
                        : {}),
                  })}
                >
                  {step.done ? (
                    <CheckIcon sx={{ fontSize: '11px' }} />
                  ) : step.attention ? (
                    <WarnIcon sx={{ fontSize: '10px' }} />
                  ) : (
                    // An idle step is a hollow ring, which firms up to the accent
                    // on the step being edited — the design's way of saying "you
                    // are here, and there is nothing wrong, there is just nothing
                    // yet".
                    <Box
                      sx={(theme: Theme) => ({
                        width: '11px',
                        height: '11px',
                        borderRadius: '50%',
                        border: `1.5px solid ${
                          isCurrent
                            ? theme.tokens.color.accent
                            : theme.tokens.componentColor.setupRailIdleRing
                        }`,
                      })}
                    />
                  )}
                </Box>
              )}

              {step.label}

              {step.count === undefined ? null : (
                <Box
                  component="span"
                  data-testid="setup-rail-count"
                  sx={(theme: Theme) => {
                    const { componentColor } = theme.tokens;
                    const palette = step.attention
                      ? {
                          bg: componentColor.countBadgeAttnBg,
                          text: componentColor.countBadgeAttnText,
                        }
                      : isCurrent
                        ? {
                            bg: componentColor.countBadgeSelectedBg,
                            text: componentColor.countBadgeSelectedText,
                          }
                        : { bg: componentColor.countBadgeBg, text: componentColor.countBadgeText };

                    return {
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px',
                      height: '20px',
                      padding: '0 6px',
                      borderRadius: `${theme.tokens.radius.pill}px`,
                      fontFamily: theme.tokens.font.mono,
                      fontSize: theme.tokens.text.counterBadge.fontSize,
                      fontWeight: theme.tokens.text.counterBadge.fontWeight,
                      backgroundColor: palette.bg,
                      color: palette.text,
                    };
                  }}
                >
                  {step.count}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
